import styles from './catalog-grid.module.scss';
import { useState } from 'react';
import { ValueFormatterParams } from 'ag-grid-community';
import RightsWrapper from '../../../rights-wrapper/rights-wrapper';
import { durationRenderer } from '../../helpers';
import WebkuaTable from '../../../webkua-table/webkua-table';
import { CreateHeaders, ErrorButton, HeaderSizeEnum, Icon, MOBILE_BREAK_POINT, PrimaryButton, currencyHeader, iconHeader, textHeader, useWindowSizeHook } from '@frontend-workspace/react-shared-lib';
import CreateNewCatalogDialog from '../create-new-catalog-dialog/create-new-catalog-dialog';
import EditCatalogDialog from '../edit-catalog-dialog/edit-catalog-dialog';
import DeleteCatalogDialog from '../delete-catalog-dialog/delete-catalog-dialog';
import CopyCatalogDialog from '../copy-catalog-dialog/copy-catalog-dialog';
import { ICatalog } from '../../../_models/catalog';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface CatalogGridProps {
  catalog: ICatalog[],
  refreshData: () => void
}

export function CatalogGrid(props: CatalogGridProps) {
  const { t } = useTranslation();
  const [viewWidth, viewHeight] = useWindowSizeHook();
  const [isNewCatalogOpen, setIsNewCatalogOpen] = useState(false);
  const [editCatalog, setEditCatalog] = useState<ICatalog | undefined>(undefined);
  const [copyCatalog, setCopyCatalog] = useState<ICatalog | undefined>(undefined);
  const [deleteCatalog, setDeleteCatalog] = useState<ICatalog | undefined>(undefined);

  const toggleNewCatalogOpen = () => setIsNewCatalogOpen(!isNewCatalogOpen);

  const openEditCatalogDialog = (e: any) => setEditCatalog(e.data);
  const closeEditCatalogDialog = () => setEditCatalog(undefined);

  const openCopyCatalogDialog = (e: any) => setCopyCatalog(e.data);
  const closeCopyCatalogDialog = () => setCopyCatalog(undefined);

  const openDeleteCatalogDialog = (e: any) => setDeleteCatalog(e.data);
  const closeDeleteCatalogDialog = () => setDeleteCatalog(undefined);

  const editRenderer = (params: ValueFormatterParams) => {
    return (
      <PrimaryButton
        variant="contained"
        size='small'
        onClick={() => openEditCatalogDialog(params)}>
        {t("standard.change")}
      </PrimaryButton>
    );
  };

  const copyRenderer = (params: ValueFormatterParams) => {
    return (
      <PrimaryButton
        variant="outlined"
        size='small'
        onClick={() => openCopyCatalogDialog(params)}>
        {t("standard.copies")}
      </PrimaryButton>
    );
  };

  const deleteRenderer = (params: ValueFormatterParams) => {
    return (
      <ErrorButton
        variant="outlined"
        size='small'
        onClick={() => openDeleteCatalogDialog(params)}>
        {t("standard.delete")}
      </ErrorButton>
    );
  };
  const priceRenderer = (params: ValueFormatterParams) => {
    return `${params?.value?.value} kr`;
  };

  return <>
    <RightsWrapper>
      <div className="d-flex flex-row align-items-center flex-wrap mb-3 mb-xl-0">
        <div className='mb-3 mb-sm-0 me-3 me-md-4'>
          <PrimaryButton
            variant="contained"
            onClick={toggleNewCatalogOpen}>
            <Icon className="me-1" fontSize="small">add</Icon>{t("standard.newCourse")}
          </PrimaryButton>
        </div>
      </div>
    </RightsWrapper>
    <WebkuaTable
      data={props.catalog}
      headers={CreateHeaders([
        ...viewWidth > MOBILE_BREAK_POINT ? [iconHeader("", "")] : [],
        textHeader("courseName", t("standard.courseName"), HeaderSizeEnum.EXTRA_LARGE),
        textHeader("duration", t("standard.duration"), HeaderSizeEnum.EXTRA_LARGE, { renderer: durationRenderer }),
        textHeader("price", t("standard.price"), HeaderSizeEnum.EXTRA_LARGE, {renderer: priceRenderer}),
        textHeader("", "", HeaderSizeEnum.SMALL, {
          renderer: editRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("", "", HeaderSizeEnum.SMALL, {
          renderer: copyRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        }),
        textHeader("", "", HeaderSizeEnum.SMALL, {
          renderer: deleteRenderer,
          cellStyle: { justifyContent: 'right' },
          headerClass: "ag-right-aligned-header"
        })
      ])}
    />
    {isNewCatalogOpen && <CreateNewCatalogDialog
      onClose={toggleNewCatalogOpen}
      open={!!isNewCatalogOpen}
      refreshData={props.refreshData}
    />}
    {editCatalog && <EditCatalogDialog
      onClose={closeEditCatalogDialog}
      open={!!editCatalog}
      refreshData={props.refreshData}
      catalogId={editCatalog.id}
    />}
    {copyCatalog && <CopyCatalogDialog
      onClose={closeCopyCatalogDialog}
      open={!!copyCatalog}
      refreshData={props.refreshData}
      catalogId={copyCatalog.id}
    />}
    {deleteCatalog && <DeleteCatalogDialog
      onClose={closeDeleteCatalogDialog}
      open={!!deleteCatalog}
      refreshData={props.refreshData}
      catalogId={deleteCatalog.id}
    />}
  </>;
}

export default CatalogGrid;